/* Pull-to-refresh — wraps a scrollable element. When the user pulls
 * down past a threshold WHILE the scroll is at the top, a thin
 * orange gradient bar appears below the topbar, ramping in opacity
 * as the pull deepens. On release past the trigger threshold, the
 * bar sweeps left-to-right continuously and the page reloads.
 *
 * Mobile-only. Touch events ignored on desktop (where there's no
 * native gesture for this); window.location.reload() is still
 * available via Cmd-R / F5. */
import React from 'react';

const TRIGGER_PX  = 70;   // pull this far + release → reload
const MAX_PULL    = 120;  // visual cap (with resistance)
const RESISTANCE  = 0.5;  // pull halves: feels heavier the further you pull

// Don't hijack scroll-to-refresh while a modal/popup is open — scrolling
// inside the dialog would otherwise bubble up here and reload the page,
// closing the popup. Covers .modal-mask dialogs + anything flagged
// data-modal-open (inline-scrim dialogs).
const isModalOpen = () =>
  typeof document !== 'undefined' && !!document.querySelector('.modal-mask, [data-modal-open]');

export const PullToRefresh = ({ scrollRef }) => {
  const [pull, setPull] = React.useState(0);
  const [refreshing, setRefreshing] = React.useState(false);
  const [topbarBottom, setTopbarBottom] = React.useState(0);
  const startYRef = React.useRef(null);
  const startXRef = React.useRef(null);
  const pullingRef = React.useRef(false);

  // The topbar's actual rendered height varies across web vs PWA
  // (safe-area-inset-top pads it). Measure it directly so the bar
  // sits FLUSH against its bottom edge — no gap, no overlap with
  // the content below the topbar.
  React.useEffect(() => {
    const measure = () => {
      const el = document.querySelector('.topbar');
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setTopbarBottom(rect.bottom);
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    // Re-measure once on next animation frame to catch the post-
    // mount layout pass (e.g. when safe-area-insets settle on PWA
    // first paint).
    const raf = requestAnimationFrame(measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      cancelAnimationFrame(raf);
    };
  }, []);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onTouchStart = (e) => {
      if (refreshing) return;
      if (isModalOpen()) return;    // a popup is open — leave scrolling to it
      if (el.scrollTop > 0) return; // only when at the top
      startYRef.current = e.touches[0].clientY;
      startXRef.current = e.touches[0].clientX;
      pullingRef.current = false;
    };
    const onTouchMove = (e) => {
      if (refreshing) return;
      if (isModalOpen()) { startYRef.current = null; if (pull) setPull(0); return; }
      if (startYRef.current == null) return;
      if (el.scrollTop > 0) {
        startYRef.current = null;
        setPull(0);
        return;
      }
      const dy = e.touches[0].clientY - startYRef.current;
      const dx = e.touches[0].clientX - (startXRef.current ?? 0);
      // Only treat this as a pull-to-refresh gesture if the motion
      // is DOMINANTLY vertical AND downward. Otherwise the user is
      // swiping horizontally inside something (e.g. the news date
      // strip) and we must not intercept — preventDefault here
      // would kill the native horizontal scroll.
      if (dy <= 0) return;
      if (Math.abs(dx) > Math.abs(dy)) return;
      e.preventDefault();
      pullingRef.current = true;
      const resisted = Math.min(dy * RESISTANCE, MAX_PULL);
      setPull(resisted);
    };
    const onTouchEnd = () => {
      if (refreshing) return;
      startYRef.current = null;
      if (pullingRef.current && pull >= TRIGGER_PX) {
        setRefreshing(true);
        setTimeout(() => { try { window.location.reload(); } catch {} }, 280);
      } else {
        setPull(0);
      }
      pullingRef.current = false;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove',  onTouchMove,  { passive: false });
    el.addEventListener('touchend',   onTouchEnd,   { passive: true });
    el.addEventListener('touchcancel',onTouchEnd,   { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove',  onTouchMove);
      el.removeEventListener('touchend',   onTouchEnd);
      el.removeEventListener('touchcancel',onTouchEnd);
    };
  }, [scrollRef, pull, refreshing]);

  // Bar opacity ramps with pull progress; reaches 1.0 at the trigger.
  // During refresh the keyframe takes over (CSS sweeps the gradient).
  const opacity = refreshing ? 1 : Math.min(1, pull / TRIGGER_PX);

  return (
    <div
      className={`pull-refresh-bar${refreshing ? ' is-refreshing' : ''}`}
      style={{
        opacity,
        // Sit a small gap below the topbar's measured bottom edge.
        // Falls back to a sensible default until measurement lands.
        top: topbarBottom ? `${topbarBottom + 10}px` : undefined,
      }}
      aria-hidden={!refreshing}
    >
      {refreshing ? 'Refreshing…' : 'Pull to refresh'}
    </div>
  );
};
